import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight, AlertTriangle, Pencil, PlusCircle } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
import EditDayModalLegal from "./EditDayModalLegal";
moment.locale("ca");

function calcWorkedMinutes(records) {
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0, entryTime = null;
  for (const r of sorted) {
    if (r.type === "entry") entryTime = new Date(r.timestamp);
    else if (r.type === "exit" && entryTime) { total += (new Date(r.timestamp) - entryTime) / 60000; entryTime = null; }
  }
  if (entryTime) total += (new Date() - entryTime) / 60000;
  return Math.max(0, Math.round(total));
}

function minutesToHHMM(mins) {
  if (!mins) return "0h 00m";
  return `${Math.floor(mins / 60)}h ${(mins % 60).toString().padStart(2, "0")}m`;
}

function getStatus(records) {
  if (!records || records.length === 0) return "absent";
  const active = records.filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  if (active.length === 0) return "absent";
  const last = active[active.length - 1];
  if (last.type === "exit") return "done";
  if (last.type === "pause_start") return "pause";
  return "active"; // entry or pause_end
}

function hasWarning(records) {
  if (!records || records.length === 0) return false;
  const active = records.filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const last = active[active.length - 1];
  if (!last) return false;
  if (last.type === "entry") {
    const diffH = (new Date() - new Date(last.timestamp)) / 3600000;
    if (diffH > 12) return "Entrada sense sortida +12h";
  }
  if (active.length >= 2) {
    const prev = active[active.length - 2];
    const diffMins = (new Date(last.timestamp) - new Date(prev.timestamp)) / 60000;
    if (prev.type === last.type && diffMins < 2) return "Registre duplicat";
  }
  return false;
}

function getEntryTime(records) {
  const active = records.filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const entry = active.find(r => r.type === "entry");
  return entry ? moment(entry.timestamp).format("HH:mm") : null;
}

function getExitTime(records) {
  const active = records.filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const exits = active.filter(r => r.type === "exit");
  return exits.length > 0 ? moment(exits[exits.length - 1].timestamp).format("HH:mm") : null;
}

const STATUS_CONFIG = {
  active: { label: "Treballant", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  pause: { label: "En pausa", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500", icon: Clock },
  done: { label: "Sortida", color: "bg-gray-100 text-gray-600 border-gray-200", dot: "bg-gray-400", icon: CheckCircle2 },
  absent: { label: "Sense fitxar", color: "bg-red-50 text-red-500 border-red-100", dot: "bg-red-400", icon: XCircle },
};

export default function AdminDayView({ venueId, members }) {
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    hosys.auth.me().then(setCurrentUser).catch(() => {});
  }, []);

  useEffect(() => {
    if (!venueId) return;
    loadRecords();
  }, [venueId, selectedDate]);

  const loadRecords = async () => {
    setLoading(true);
    const prevDate = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
    const all = await hosys.entities.TimeRecord.filter({
      venue_id: venueId,
      date_from: prevDate,
      date_to: selectedDate,
      limit: 2000,
    });
    setRecords(all);
    setLoading(false);
  };

  const getMemberRecords = (email) => {
    const prevDate = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
    const midnightSelected = moment(selectedDate).startOf("day");
    const memberRecs = records.filter(r => r.user_email === email);
    const prevRecs = memberRecs.filter(r => r.date === prevDate && !r.is_deleted);
    const todayRecs = memberRecs.filter(r => r.date === selectedDate && !r.is_deleted);
    
    // Include yesterday if night shift crosses midnight
    const hasNightShift = (() => {
      const sorted = [...prevRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      if (!sorted.length) return false;
      const last = sorted[sorted.length - 1];
      if (last.type === "entry" || last.type === "pause_start" || last.type === "pause_end") return true;
      if (last.type === "exit" && moment(last.timestamp).isSameOrAfter(midnightSelected)) return true;
      return false;
    })();
    
    return hasNightShift
      ? [...prevRecs, ...todayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      : todayRecs;
  };

  const prevDay = () => setSelectedDate(d => moment(d).subtract(1, "day").format("YYYY-MM-DD"));
  const nextDay = () => setSelectedDate(d => moment(d).add(1, "day").format("YYYY-MM-DD"));
  const isToday = selectedDate === moment().format("YYYY-MM-DD");

  const checkedIn = members.filter(m => getStatus(getMemberRecords(m.user_email)) !== "absent");
  const absent = members.filter(m => getStatus(getMemberRecords(m.user_email)) === "absent");
  const totalHours = members.reduce((acc, m) => acc + calcWorkedMinutes(getMemberRecords(m.user_email)), 0);

  return (
    <div className="space-y-4">
      {/* Date navigator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <button onClick={prevDay} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="font-bold text-gray-900 capitalize">{moment(selectedDate).format("dddd, D MMMM YYYY")}</p>
          {isToday && <p className="text-xs text-[#7B2D3F] font-medium">Avui</p>}
        </div>
        <button onClick={nextDay} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500" disabled={isToday}>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-2xl font-bold text-emerald-700">{checkedIn.length}</p>
          <p className="text-xs text-emerald-600 font-medium">Han fitxat</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 text-center border border-red-100">
          <p className="text-2xl font-bold text-red-500">{absent.length}</p>
          <p className="text-xs text-red-500 font-medium">Sense fitxar</p>
        </div>
        <div className="bg-[#7B2D3F]/5 rounded-xl p-3 text-center border border-[#7B2D3F]/10">
          <p className="text-2xl font-bold text-[#7B2D3F]">{minutesToHHMM(totalHours)}</p>
          <p className="text-xs text-[#7B2D3F] font-medium">Hores totals</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Carregant...</div>
      ) : (
        <div className="space-y-2">
          {members.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">Sense treballadors configurats</div>
          )}
          {members.map(member => {
            const mRecs = getMemberRecords(member.user_email);
            const status = getStatus(mRecs);
            const worked = calcWorkedMinutes(mRecs);
            const entry = getEntryTime(mRecs);
            const exit = getExitTime(mRecs);
            const warn = hasWarning(mRecs);
            const cfg = STATUS_CONFIG[status];
            const Icon = cfg.icon;

            return (
              <div key={member.id} className={`bg-white rounded-xl border shadow-sm p-4 ${warn ? "border-amber-200" : "border-gray-100"}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7B2D3F]/10 to-[#7B2D3F]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#7B2D3F] font-bold text-sm">
                      {(member.user_name || member.user_email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm truncate">{member.user_name || member.user_email}</p>
                    {member.position && <p className="text-xs text-gray-400">{member.position}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                      {worked > 0 && (
                        <span className="text-xs font-bold text-gray-700">{minutesToHHMM(worked)}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setEditingMember(member)}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#7B2D3F] transition-colors"
                      title="Editar fitxatges"
                    >
                      {status === "absent" ? <PlusCircle className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Entry/Exit times */}
                {(entry || exit) && (
                  <div className="flex gap-4 mt-3 pt-3 border-t border-gray-50">
                    {entry && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 bg-emerald-100 rounded-md flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                        </div>
                        <span className="text-xs text-gray-500">Entrada <span className="font-bold text-gray-700">{entry}</span></span>
                      </div>
                    )}
                    {exit && (
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 bg-rose-100 rounded-md flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-rose-500 rounded-full" />
                        </div>
                        <span className="text-xs text-gray-500">Sortida <span className="font-bold text-gray-700">{exit}</span></span>
                      </div>
                    )}
                  </div>
                )}

                {/* Warning */}
                {warn && (
                  <div className="flex items-center gap-1.5 mt-2 text-amber-600">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    <p className="text-xs font-medium">{warn}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingMember && currentUser && (
        <EditDayModalLegal
          member={editingMember}
          date={selectedDate}
          records={records.filter(r => r.user_email === editingMember.user_email && !r.is_deleted)}
          venueId={venueId}
          currentUser={currentUser}
          onClose={() => setEditingMember(null)}
          onSaved={() => { setEditingMember(null); loadRecords(); }}
        />
      )}
    </div>
  );
}